/**
 * 会员档位阶梯：前端套餐 key（free/vip/svip/ultra）与后端 memberType（free/pro/plus/ultra）统一收口。
 * 新增档位时只改 MEMBER_TIER_ORDER 与别名映射，业务侧用 meetsMinTier / hasMembershipAtLeast 比较。
 */

/** Pinia `highestPackage`、支付弹窗等使用的展示键 */
export const MEMBER_PACKAGE_KEYS = ['free', 'vip', 'svip', 'ultra'] as const;
export type MemberPackageKey = (typeof MEMBER_PACKAGE_KEYS)[number];

/** 后端 memberType / 订单 orderKey */
export const BACKEND_MEMBER_TYPES = ['free', 'pro', 'plus', 'ultra'] as const;
export type BackendMemberType = (typeof BACKEND_MEMBER_TYPES)[number];

/** 从低到高的唯一顺序表；新增档位只追加在此 */
export const MEMBER_TIER_ORDER = [...MEMBER_PACKAGE_KEYS] as const;

const PACKAGE_TO_BACKEND: Record<MemberPackageKey, BackendMemberType> = {
  free: 'free',
  vip: 'pro',
  svip: 'plus',
  ultra: 'ultra'
};

const BACKEND_TO_PACKAGE: Record<BackendMemberType, MemberPackageKey> = {
  free: 'free',
  pro: 'vip',
  plus: 'svip',
  ultra: 'ultra'
};

const TIER_RANK = Object.fromEntries(
  MEMBER_TIER_ORDER.map((key, index) => [key, index])
) as Record<MemberPackageKey, number>;

const PACKAGE_KEY_ALIASES: Record<string, MemberPackageKey> = {
  free: 'free',
  vip: 'vip',
  svip: 'svip',
  ultra: 'ultra',
  vvip: 'ultra',
  pro: 'vip',
  plus: 'svip'
};

const BACKEND_TYPE_ALIASES: Record<string, BackendMemberType> = {
  free: 'free',
  pro: 'pro',
  plus: 'plus',
  ultra: 'ultra',
  vip: 'pro',
  svip: 'plus',
  vvip: 'ultra'
};

/** 任意套餐 / memberType 字面量 → 展示键 */
export function normalizePackageKey(kind: string | undefined | null): MemberPackageKey {
  if (kind == null || kind === '') {
    return 'free';
  }
  return PACKAGE_KEY_ALIASES[kind] ?? 'free';
}

/** 任意套餐 / memberType 字面量 → 后端 memberType */
export function normalizeMemberType(kind: string | undefined | null): BackendMemberType {
  if (kind == null || kind === '') {
    return 'free';
  }
  return BACKEND_TYPE_ALIASES[kind] ?? 'free';
}

export function packageKeyToMemberType(key: MemberPackageKey): BackendMemberType {
  return PACKAGE_TO_BACKEND[key];
}

export function memberTypeToPackageKey(type: BackendMemberType): MemberPackageKey {
  return BACKEND_TO_PACKAGE[type];
}

/** 档位排序权重（越大越高） */
export function membershipTierRank(kind: string | undefined | null): number {
  return TIER_RANK[normalizePackageKey(kind)];
}

/** 当前档位是否不低于最低要求（含相等） */
export function meetsMinTier(
  current: string | undefined | null,
  min: string | undefined | null
): boolean {
  return membershipTierRank(current) >= membershipTierRank(min);
}

/** 当前档位是否严格低于目标档位 */
export function isBelowTier(
  current: string | undefined | null,
  target: string | undefined | null
): boolean {
  return membershipTierRank(current) < membershipTierRank(target);
}

/** 当前档位是否严格高于目标档位 */
export function isAboveTier(
  current: string | undefined | null,
  target: string | undefined | null
): boolean {
  return membershipTierRank(current) > membershipTierRank(target);
}

export function compareTiers(
  left: string | undefined | null,
  right: string | undefined | null
): -1 | 0 | 1 {
  const diff = membershipTierRank(left) - membershipTierRank(right);
  if (diff < 0) return -1;
  if (diff > 0) return 1;
  return 0;
}

/** 业务权益最低档位注册表：新增权益只登记 minTier，判断走 meetsMinTier */
export const MEMBER_ENTITLEMENT_MIN_TIER = {
  paidMember: 'vip',
  styleTrainDiscount: 'svip',
  styleRenderTrainDiscount: 'svip',
  trainMaxImagesSvip: 'svip',
  trainMaxImagesUltra: 'ultra',
  maxStorageTier: 'ultra'
} as const satisfies Record<string, MemberPackageKey>;

export type MemberEntitlementKey = keyof typeof MEMBER_ENTITLEMENT_MIN_TIER;

export function hasMemberEntitlement(
  current: string | undefined | null,
  entitlement: MemberEntitlementKey
): boolean {
  return meetsMinTier(current, MEMBER_ENTITLEMENT_MIN_TIER[entitlement]);
}

/** 从含 memberType 字段的数组中，找到最高档位对应的元素；空数组返回 undefined */
export function findHighestTierItem<T extends { memberType: string }>(
  items: T[]
): T | undefined {
  if (items.length === 0) return undefined;
  return items.reduce((highest, item) =>
    membershipTierRank(item.memberType) > membershipTierRank(highest.memberType)
      ? item
      : highest
  , items[0]);
}

/** 从含 memberType 字段的数组中，解析最高档位的展示键；空数组返回 'free' */
export function resolveHighestPackageKey<T extends { memberType: string }>(
  items: T[]
): MemberPackageKey {
  const highest = findHighestTierItem(items);
  return highest ? normalizePackageKey(highest.memberType) : 'free';
}

/**
 * 有效会员档位：优先 packages（highestPackage）；
 * 团队上下文且 packages 为空时，兜底 group.memberType（与 VipStatus 一致）。
 * 团队旗舰版等场景下 memberList 可能未带档位，仅看 highestPackage 会误判为 free。
 */
export function resolveEffectivePackageKey(input: {
  highestPackage?: string | null;
  teamMemberType?: string | null;
  isTeamContext?: boolean;
}): MemberPackageKey {
  const fromPackages = normalizePackageKey(input.highestPackage);
  if (fromPackages !== 'free') return fromPackages;
  if (!input.isTeamContext) return fromPackages;
  const fromTeam = normalizePackageKey(input.teamMemberType);
  return fromTeam !== 'free' ? fromTeam : fromPackages;
}
